import { useEffect, useState } from "react";
import { Card, Button, Table, Form, Tag, Popconfirm, Spin } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, DeleteOutlined, FundOutlined } from "@ant-design/icons";
import moment from "moment";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";

import {
  generatePairingCode,
  getBranchDevices,
  getBranches,
  unbindDevice,
} from "../../../network/network";
import PairingCodeModal from "./components/PairingCodeModal";
import { handleApiError } from "../../../utils/errorHandler";
import { DeviceType } from "../../../configs/constants";

const KitchenDisplayScreens = () => {
  const { t } = useTranslation();

  const [kdsDevicesData, setKdsDevicesData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);

  const [apiResponse, setApiResponse] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const [kdsModalOpen, setKdsModalOpen] = useState(false);

  const [dataLoading, setDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);

  const [form] = Form.useForm();

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "kitchen-display-screens",
      breadcrumbName: t("kitchen_display_screens"),
      icon: <FundOutlined />,
    },
  ];

  useEffect(() => {
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response?.data?.data?.branches || []);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    setDataLoading(true);
    getBranchDevices()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        let devices = response?.data?.data?.devices || [];
        devices = devices.filter((d) => d.device_type === DeviceType.Kds);
        setKdsDevicesData(devices);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setDataLoading(false);
      });
  }, [reloadTableData, t]);

  useEffect(() => {
    if (!apiResponse?.expires_at) return;

    const firstDiff = moment(apiResponse.expires_at).diff(moment(), "seconds");
    setRemainingSeconds(firstDiff > 0 ? firstDiff : 0);

    const interval = setInterval(() => {
      const diff = moment(apiResponse.expires_at).diff(moment(), "seconds");
      setRemainingSeconds(diff > 0 ? diff : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [apiResponse]);

  const openCreateKDSModal = () => {
    setKdsModalOpen(true);
    form.resetFields();
    setApiResponse(null);
    setRemainingSeconds(0);
  };

  const closeCreateKDSModal = () => {
    setKdsModalOpen(false);
    form.resetFields();
    setApiResponse(null);
    setRemainingSeconds(0);
    setReloadTableData(!reloadTableData);
  };

  const handleCreateKDS = () => {
    form.validateFields().then((values) => {
      setDataLoading(true);

      const payload = {
        device_type: DeviceType.Kds,
        device_name: values.device_name?.trim() || undefined,
        branch_id: values.branch_id,
      };

      generatePairingCode(payload)
        .then((response) => {
          let data = response.data || response.response.data;

          if (data.success === true) {
            setApiResponse(data.data);

            const diff = moment(data.data.expires_at).diff(moment(), "seconds");
            setRemainingSeconds(diff > 0 ? diff : 0);
          } else {
            handleApiError(response, t);
          }
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => {
          setDataLoading(false);
        });
    });
  };

  const handleUnbind = (uid) => {
    unbindDevice(uid)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          NotificationWithIcon(
            "success",
            t("device_unbind"),
            t("device_unbind_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const kdsColumns = [
    {
      title: "#",
      className: "app-table-index-column",
      render: (t, r, index) => <>{index + 1}</>,
    },
    {
      title: t("type"),
      dataIndex: "device_type",
      render: () => <Tag color="geekblue">{t("kitchen_display_screen")}</Tag>,
    },
    {
      title: t("name"),
      dataIndex: "device_name",
      render: (v) => v || "-",
    },
    {
      title: t("bound_by"),
      dataIndex: "bound_by",
      render: (v) => v || "-",
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return branch?.name;
      },
    },
    {
      title: t("last_seen"),
      dataIndex: "last_seen_at",
      render: (v) => (v ? moment(v).format("MMM D, YYYY hh:mm a") : "-"),
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (v) => (v ? moment(v).format("MMM D, YYYY hh:mm a") : "-"),
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => (
        <Popconfirm
          title={t("unbind_this_device")}
          okText={t("yes")}
          cancelText={t("no")}
          placement="topLeft"
          onConfirm={() => handleUnbind(record.uid)}
        >
          <Button type="link" title={t("delete_entity")}>
            <DeleteOutlined />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Spin spinning={dataLoading}>
        <Card className="app-mb-20">
          <CustomBreadcrumb menus={breadcrumbRoutes} />
          <PageHeader
            className="app-p-0"
            title={t("kitchen_display_screens")}
            ghost={false}
            extra={[
              <Button key="add-kds" type="primary" onClick={openCreateKDSModal}>
                {t("add_kitchen_screen")}
              </Button>,
            ]}
          />
        </Card>

        <Card>
          <Table
            columns={kdsColumns}
            dataSource={kdsDevicesData}
            rowKey="uid"
            pagination={false}
          />
        </Card>

        <PairingCodeModal
          open={kdsModalOpen}
          title={t("add_kitchen_screen")}
          onCancel={closeCreateKDSModal}
          form={form}
          branchesData={branchesData}
          dataLoading={dataLoading}
          apiResponse={apiResponse}
          remainingSeconds={remainingSeconds}
          onGenerate={handleCreateKDS}
          onResetPairing={() => {
            setApiResponse(null);
            setRemainingSeconds(0);
          }}
          deviceType={DeviceType.Kds}
        />
      </Spin>
    </>
  );
};

export default KitchenDisplayScreens;
